import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Button Variants - Sistema de Cores Consistente
 * 
 * PRIMÁRIO (CTAs principais):
 * - brand-gradient: Rosa brand gradient - para ações principais de conversão
 * 
 * SECUNDÁRIO:
 * - secondary: Para ações secundárias em fundos claros
 * - ghost-dark: Para ações secundárias em fundos escuros
 * 
 * ESPECIAIS:
 * - gem: Roxo para ações relacionadas a gemas
 * - destructive: Vermelho para ações destrutivas
 * 
 * ADMIN:
 * - admin-primary: Mesma cor de brand-gradient
 * - admin-outline: Borda branca para contraste em dark theme
 * - admin-secondary: Background translúcido
 * 
 * DEPRECADOS (não usar em código novo):
 * - luxury: Use brand-gradient
 * - purple: Use gem
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Core variants with explicit colors
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "text-foreground bg-transparent hover:bg-accent hover:text-accent-foreground",
        link: "text-primary bg-transparent underline-offset-4 hover:underline",
        light: "bg-white text-black hover:bg-white/90 [&_svg]:text-black",
        
        // Brand gradient - primary CTA
        "brand-gradient": "bg-gradient-to-b from-[#C4406B] to-[#9F3764] text-white hover:from-[#9F3764] hover:to-[#8B1538] shadow-lg shadow-luxury-pink/30 border-0",
        
        // Gem variant - purple for gem-related actions
        "gem": "bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:brightness-110 border-0",
        
        // Ghost for dark theme
        "ghost-dark": "text-white/60 bg-transparent hover:text-white hover:bg-white/5",
        
        // Admin variants
        "admin-primary": "bg-gradient-to-b from-[#C4406B] to-[#9F3764] text-white hover:from-[#9F3764] hover:to-[#8B1538] border-0",
        "admin-outline": "border-2 border-white/30 text-white bg-transparent hover:bg-white/10 hover:border-white/50",
        "admin-secondary": "bg-white/15 text-white hover:bg-white/25 border-0",
        
        // DEPRECATED - use brand-gradient instead
        luxury: "bg-gradient-to-b from-[#C4406B] to-[#9F3764] text-white hover:from-[#9F3764] hover:to-[#8B1538] border-0",
        // DEPRECATED - use gem instead  
        purple: "bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:brightness-110 border-0",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
